/**
 * `/holdings/symbol/{symbol}` — the 13F view of one symbol
 * (`services/qv-data/app/holdings/read.py`, `docs/plans/holdings-13f.md`).
 *
 * **Every field is mapped by name.** The route returns more than this view
 * uses, and a mapper that spread the payload would carry fields nobody
 * declared; one that listed some would silently drop the next one the server
 * added. Listed in full, so a field the server renames fails here, in a type.
 *
 * The chart tier serves it, the same host as `/sym` — so the base is the same
 * one `api/symbols.ts` uses, and in dev `/holdings` is in Vite's proxy list.
 */
import { requestHeaders } from "../lib/user";
import { httpBase } from "./backend";

const BASE = (httpBase(import.meta.env.VITE_QV_SYM_URL, import.meta.env.VITE_QV_SET_URL) || "").replace(/\/$/, "");

export type HoldingStatus = "NEW" | "ADD" | "HOLD" | "TRIM" | "EXIT" | null;

/** One fund's position in one quarter. Dollars; `trade` is the price move removed. */
export interface Holding {
  fund: string;
  manager: string;
  fundName: string;
  fundOrder: number | null;
  period: string;
  filed: string | null;
  value: number;
  shares: number | null;
  calls: number;
  puts: number;
  trade: number | null;
  status: HoldingStatus;
  pctBook: number | null;
  tradePctBook: number | null;
  pctVol: number | null;
  pctOut: number | null;
  /** The filer's share book as a part of its whole 13F; the rest is options. */
  stockShare: number | null;
}

export interface LedgerMove {
  fund: string;
  fundName: string;
  trade: number;
  status: HoldingStatus;
}

/** One quarter of adds and cuts, newest first in `HoldingsSymbol.ledger`. */
export interface LedgerQuarter {
  period: string;
  label: string;
  close: number | null;
  bought: number;
  sold: number;
  /** The newest quarter, while not every fund has filed it. */
  pending: boolean;
  adds: LedgerMove[];
  cuts: LedgerMove[];
}

export interface HoldingsWindow {
  qn: string;
  base: string;
  window1Q: string;
  window2Q: string;
  reported1Q: number;
  filedFunds1Q: number;
  funds: number;
  built: string | null;
}

export type HoldingsState = "held" | "exited" | "options" | "never";

export interface HoldingsSymbol {
  symbol: string;
  state: HoldingsState;
  window: HoldingsWindow;
  quarters: string[];
  holders: Holding[];
  exited: Holding[];
  optionsOnly: Holding[];
  ledger: LedgerQuarter[];
  /** Each fund's value at each of `quarters` — null where it filed no row for the symbol. */
  history: Record<string, (number | null)[]>;
  /** Every fund in `history`, named — including funds that have since left. */
  funds: Record<string, { name: string; manager: string; order: number | null }>;
  /** Every 13F filer but the fifteen, each at its latest filed quarter — null
   *  where the every-filer files have not reached this server. */
  others: OtherHolders | null;
}

/** Everyone else on one symbol, beside the Top 15's own totals. */
export interface OtherHolders {
  holders: number;
  value: number;
  pctOut: number | null;
  top15Holders: number;
  top15Value: number;
  top15PctOut: number | null;
  /** The largest, by $ held; `fund` is the filer's CIK. */
  rows: Holding[];
  /** The largest exits in the filers' latest quarter. */
  exits: Holding[];
}

/** One fund's page: one of the fifteen by id, anyone else by CIK. */
export interface FundPage {
  fund: { id: string; manager: string; fund: string; fundId: string | null; stockShare: number | null };
  period: string | null;
  /** Its latest 13F is older than the newest quarter. */
  stale: boolean;
  book: number;
  bought: number;
  sold: number;
  positions: number;
  top: FundRow[];
  buys: FundRow[];
  sells: FundRow[];
  /** Quarter by quarter, for the charts — null until the history files are pulled. */
  history: FundHistory | null;
}

/** A holding on a fund page, with what the heatmap groups by. */
export type FundRow = Holding & { symbol: string; sector: string | null; industry: string | null; name: string | null };

export interface FundQuarter {
  period: string;
  label: string;
  book: number | null;
  positions: number | null;
  /** This quarter's book held to the next quarter end, price only; null for the newest. */
  ret: number | null;
  spyRet: number | null;
  bought: number | null;
  sold: number | null;
  new: number | null;
  add: number | null;
  trim: number | null;
  exit: number | null;
}

export interface FundMove { symbol: string; trade: number; status: "NEW" | "EXIT" | null }

export interface FundHistory {
  quarters: FundQuarter[];
  /** period -> sector -> share of the stock book */
  sectors: Record<string, Record<string, number>>;
  /** period -> that quarter's own top 5 */
  top: Record<string, Record<string, number>>;
  /** period -> today's top 5, their weight then */
  now: Record<string, Record<string, number>>;
  /** window (YTD, 12M, 2025, 2Y, 3Y) -> the fund's and SPY's; value null = needs more history */
  returns: Record<string, { value: number | null; spy: number | null }>;
  /** the last four quarters' biggest buys and sells */
  moves: { period: string; label: string; buys: FundMove[]; sells: FundMove[] }[];
}

/** `undefined` when there is no answer — unknown fund, offline, or not pulled yet. */
export async function fetchFund(fund: string, limit = 100): Promise<FundPage | undefined> {
  if (!BASE || !fund) return undefined;
  try {
    const res = await fetch(`${BASE}/holdings/fund/${encodeURIComponent(fund)}?limit=${limit}`, {
      headers: await requestHeaders(),
    });
    if (!res.ok) return undefined;
    return mapFund(await res.json());
  } catch {
    return undefined;
  }
}

/** `undefined` when there is no answer — unconfigured, offline, or not pulled yet (503). */
export async function fetchHoldings(symbol: string): Promise<HoldingsSymbol | undefined> {
  if (!BASE || !symbol) return undefined;
  try {
    const res = await fetch(`${BASE}/holdings/symbol/${encodeURIComponent(symbol)}`, {
      headers: await requestHeaders(),
    });
    if (!res.ok) return undefined;
    return mapHoldings(await res.json());
  } catch {
    return undefined;
  }
}

/* eslint-disable @typescript-eslint/no-explicit-any */
const num = (v: any): number => (typeof v === "number" && Number.isFinite(v) ? v : 0);
const opt = (v: any): number | null => (typeof v === "number" && Number.isFinite(v) ? v : null);

function mapHolding(h: any): Holding {
  return {
    fund: String(h.fund), manager: String(h.manager), fundName: String(h.fundName),
    fundOrder: opt(h.fundOrder), period: String(h.period), filed: h.filed ?? null,
    value: num(h.value), shares: opt(h.shares), calls: num(h.calls), puts: num(h.puts),
    trade: opt(h.trade), status: (h.status ?? null) as HoldingStatus,
    pctBook: opt(h.pctBook), tradePctBook: opt(h.tradePctBook), pctVol: opt(h.pctVol), pctOut: opt(h.pctOut),
    stockShare: opt(h.stockShare),
  };
}

const mapSymbolRow = (h: any): FundRow => ({
  ...mapHolding(h), symbol: String(h.symbol), sector: h.sector ?? null, industry: h.industry ?? null, name: h.name ?? null,
});

const numMap = (o: any): Record<string, number> =>
  Object.fromEntries(Object.entries(o ?? {}).map(([k, v]) => [k, num(v)]));
const nested = (o: any): Record<string, Record<string, number>> =>
  Object.fromEntries(Object.entries(o ?? {}).map(([k, v]) => [k, numMap(v)]));
const mapMoveRow = (m: any): FundMove => ({ symbol: String(m.symbol), trade: num(m.trade), status: m.status ?? null });

function mapHistory(h: any): FundHistory | null {
  if (!h) return null;
  return {
    quarters: (h.quarters ?? []).map((q: any): FundQuarter => ({
      period: String(q.period), label: String(q.label), book: opt(q.book), positions: opt(q.positions),
      ret: opt(q.ret), spyRet: opt(q.spyRet), bought: opt(q.bought), sold: opt(q.sold),
      new: opt(q.new), add: opt(q.add), trim: opt(q.trim), exit: opt(q.exit),
    })),
    sectors: nested(h.sectors), top: nested(h.top), now: nested(h.now),
    returns: Object.fromEntries(Object.entries(h.returns ?? {}).map(([k, v]: [string, any]) =>
      [k, { value: opt(v?.value), spy: opt(v?.spy) }])),
    moves: (h.moves ?? []).map((m: any) => ({
      period: String(m.period), label: String(m.label),
      buys: (m.buys ?? []).map(mapMoveRow), sells: (m.sells ?? []).map(mapMoveRow),
    })),
  };
}

export function mapFund(j: any): FundPage {
  const f = j.fund ?? {};
  // the fifteen carry a year of totals; anyone else one — the latest is the last either way
  const t = (j.totals ?? []).slice(-1)[0] ?? {};
  return {
    fund: { id: String(f.id), manager: String(f.manager ?? f.id), fund: String(f.fund ?? f.manager ?? f.id),
            // the fifteen are named by their own id and carry a display order
            fundId: f.fundId ?? (typeof f.order === "number" ? String(f.id) : null), stockShare: opt(f.stockShare) },
    period: j.period ?? null, stale: Boolean(j.stale),
    book: num(t.value), bought: num(t.bought), sold: num(t.sold), positions: num(j.positions),
    top: (j.top ?? []).map(mapSymbolRow), buys: (j.buys ?? []).map(mapSymbolRow), sells: (j.sells ?? []).map(mapSymbolRow),
    history: mapHistory(j.history),
  };
}

function mapOthers(o: any): OtherHolders | null {
  if (!o) return null;
  return {
    holders: num(o.holders), value: num(o.value), pctOut: opt(o.pctOut),
    top15Holders: num(o.top15Holders), top15Value: num(o.top15Value), top15PctOut: opt(o.top15PctOut),
    rows: (o.rows ?? []).map(mapHolding), exits: (o.exits ?? []).map(mapHolding),
  };
}

const mapMove = (m: any): LedgerMove => ({
  fund: String(m.fund), fundName: String(m.fundName), trade: num(m.trade), status: (m.status ?? null) as HoldingStatus,
});

export function mapHoldings(j: any): HoldingsSymbol {
  const w = j.window ?? {};
  return {
    symbol: String(j.symbol),
    state: j.state as HoldingsState,
    window: {
      qn: String(w.qn), base: String(w.base), window1Q: String(w.window1Q), window2Q: String(w.window2Q),
      reported1Q: num(w.reported1Q), filedFunds1Q: num(w.filedFunds1Q), funds: num(w.funds), built: w.built ?? null,
    },
    quarters: (j.quarters ?? []).map(String),
    holders: (j.holders ?? []).map(mapHolding),
    exited: (j.exited ?? []).map(mapHolding),
    optionsOnly: (j.optionsOnly ?? []).map(mapHolding),
    history: Object.fromEntries(Object.entries(j.history ?? {}).map(([k, v]) => [k, (Array.isArray(v) ? v : []).map(opt)])),
    funds: Object.fromEntries(Object.entries(j.funds ?? {}).map(([k, f]: [string, any]) =>
      [k, { name: String(f?.name ?? k), manager: String(f?.manager ?? k), order: opt(f?.order) }])),
    others: mapOthers(j.others),
    ledger: (j.ledger ?? []).map((q: any): LedgerQuarter => ({
      period: String(q.period), label: String(q.label), close: opt(q.close),
      bought: num(q.bought), sold: num(q.sold), pending: Boolean(q.pending),
      adds: (q.adds ?? []).map(mapMove), cuts: (q.cuts ?? []).map(mapMove),
    })),
  };
}

/** One search hit: one of the fifteen (`top15`) or any other filer, by CIK. */
export interface FundHit {
  id: string;
  manager: string;
  value: number;
  positions: number;
  period: string | null;
  top15: boolean;
}

/** `/holdings/funds?q=` — the fifteen that match, then other filers, largest book first. */
export async function searchFunds(q: string): Promise<FundHit[]> {
  if (!BASE || !q.trim()) return [];
  try {
    const res = await fetch(`${BASE}/holdings/funds?q=${encodeURIComponent(q)}`, { headers: await requestHeaders() });
    if (!res.ok) return [];
    const j = await res.json();
    const hit = (top15: boolean) => (f: any): FundHit => ({
      id: String(f.id), manager: String(top15 ? f.fund ?? f.manager : f.manager), value: num(f.value),
      positions: num(f.positions), period: f.period ?? null, top15,
    });
    return [...(j.funds ?? []).map(hit(true)), ...(j.others ?? []).map(hit(false))];
  } catch {
    return [];
  }
}
